import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';
import { CalloutBox, StatCard, DataTable } from '../primitives';
import { SectionProps } from './types';
import { Citation } from '../Citation';

const decadeReturns = [
  { decade: '1930s', return: '-0.5%', context: 'Great Depression' },
  { decade: '1940s', return: '+5.8%', context: 'WWII, post-war boom' },
  { decade: '1950s', return: '+15.5%', context: 'Post-war golden era' },
  { decade: '1960s', return: '+5.8%', context: 'Kennedy/LBJ era' },
  { decade: '1970s', return: '-2.6%', context: 'Stagflation, oil crisis' },
  { decade: '1980s', return: '+12.7%', context: 'Reagan bull market' },
  { decade: '1990s', return: '+16.3%', context: 'Tech boom' },
  { decade: '2000s', return: '-2.8%', context: 'Dot-com crash, 2008 crisis' },
  { decade: '2010s', return: '+11.2%', context: 'Post-crisis recovery' },
  { decade: '2020s*', return: '+12.5%', context: 'COVID recovery, AI boom' },
];

export const StockMarketSection: React.FC<Pick<SectionProps, 'citations' | 'assumptions' | 'formatCurrency'>> = ({
  citations,
  assumptions,
  formatCurrency
}) => {
  return (
    <ReportSection id="stockmarket" title="The Stock Market Alternative" subtitle="What does 'investing in the market' actually mean?">
      <p className="text-gray-700 mb-6">
        If you sell the property, the natural alternative is to invest in a diversified portfolio of stocks
        and bonds. But what does that actually mean, and what should you expect?
      </p>

      <ReportSubsection title="What You're Actually Buying">
        <p className="text-gray-700 mb-4">
          When you buy an S&P 500 index fund, you're buying tiny ownership stakes in the 500 largest
          publicly-traded American companies. Apple, Microsoft, Amazon, Google, Berkshire Hathaway—you
          own a piece of all of them. Your returns come from two sources:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
          <li><strong>Dividends:</strong> Companies pay out a portion of their profits to shareholders</li>
          <li><strong>Price appreciation:</strong> The stock price goes up as companies grow earnings</li>
        </ul>

        <p className="text-gray-700">
          Unlike a single rental property, you're instantly diversified across hundreds of companies in
          dozens of industries. If one company fails, it barely affects your portfolio.
        </p>
      </ReportSubsection>

      <ReportSubsection title="Historical Returns by Decade">
        <p className="text-gray-700 mb-4">
          Let's look at what the <Citation citation={citations.nyuStern}>S&P 500 has actually returned</Citation>,
          decade by decade, adjusted for inflation:
        </p>

        <DataTable
          columns={[
            { key: 'decade', header: 'Decade', align: 'left' },
            { key: 'return', header: 'Real Return', align: 'right' },
            { key: 'context', header: 'Context', align: 'left', muted: true }
          ]}
          data={decadeReturns}
          caption="*2020 through Dec 2025. Earlier decades from NYU Stern."
        />

        <p className="text-gray-700 mt-4 mb-4">
          The long-term average is around <span className="font-semibold text-gray-900 border-b border-dotted border-gray-400">7.5% real (after inflation)</span>. But look at the
          variation! The 1950s and 1990s delivered 15%+ annually. The 1970s and 2000s were negative.
          If you happened to invest at the wrong time, you could go an entire decade with no gains.
        </p>
      </ReportSubsection>

      <ReportSubsection title="The CAPE Ratio: Are Stocks Expensive?">
        <p className="text-gray-700 mb-4">
          One way to gauge whether stocks are expensive is the <Citation citation={citations.shillerCAPE}>Shiller
          CAPE ratio</Citation> (Cyclically Adjusted Price-to-Earnings). Unlike a regular P/E ratio that uses
          one year of earnings, CAPE divides the current price by the <em>average</em> of the last 10 years of
          earnings (adjusted for inflation). This smooths out business cycles—a company might have a bad year,
          but CAPE looks at the full decade.
        </p>

        <p className="text-gray-700 mb-4">
          The logic: when CAPE is high, you're paying more for each dollar of (smoothed) earnings, which
          historically means lower future returns. When CAPE is low, stocks are "cheap" and future returns
          tend to be higher. As of late 2025, the CAPE ratio is around <span className="font-semibold text-gray-900 border-b border-dotted border-gray-400">40</span>.
        </p>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard label="Current CAPE" value="~40" sublabel="December 2025" />
          <StatCard label="Historical Average" value="~17" sublabel="Since 1880" />
          <StatCard label="Previous Peak" value="44" sublabel="December 1999" />
        </div>

        <p className="text-gray-700 mb-4">
          That current level of 40 is higher than any time in history except the peak of the dot-com bubble.
          Historically, when CAPE is this high, subsequent 10-year returns tend to be lower—often in the
          3-5% real range rather than the historical 7% average.
        </p>

        <p className="text-gray-700 mb-4">
          <strong>But here's the complication:</strong> CAPE has been elevated for over a decade now. It crossed
          above 25 in 2014 and has stayed there since—yet the market returned ~12% annually from 2014-2024.
          Some argue CAPE has become a poor predictor because the market is "structurally different": tech
          companies have higher margins than historical industrials, accounting rules have changed, and low
          interest rates (until recently) justified higher valuations.
        </p>

        <CalloutBox type="warning" title="What This Means">
          <p className="mb-2">
            We're not predicting a crash. Markets can stay expensive for a long time—as the past decade proves.
            But it's worth knowing that if you're investing {formatCurrency(assumptions.propertyValue)} today, historical patterns suggest you might
            get below-average returns over the next decade. The 7% historical average may be optimistic.
          </p>
          <p>
            Then again, people have been saying this since 2015, and anyone who sold then missed one of the best
            decades in market history. CAPE tells you valuations are high; it doesn't tell you <em>when</em> returns
            will disappoint.
          </p>
        </CalloutBox>
      </ReportSubsection>

      <ReportSubsection title="Tech Stocks vs. The Broad Market">
        <p className="text-gray-700 mb-4">
          You might wonder: should you just invest in tech stocks? After all, the <Citation citation={citations.nasdaq}>
          NASDAQ-100</Citation> has dramatically outperformed the S&P 500 over the past 15 years:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
          <li>2009-2021: NASDAQ returned ~20% annually vs. S&P 500's ~14%</li>
          <li>2023: NASDAQ up 55% (best year since 1999) vs. S&P 500's ~24%</li>
          <li>NASDAQ outperformed in 12 of 16 calendar years since 2008</li>
        </ul>

        <p className="text-gray-700 mb-4">
          Sounds great! But there's a catch. The same concentration that drives outperformance in good times
          causes underperformance in bad times. The NASDAQ lost 78% from 2000-2002 and took 15 years to recover.
          In 2022, it fell 33% while the S&P 500 fell 19%.
        </p>

        <p className="text-gray-700">
          The question isn't whether tech will continue to outperform—it might or might not. The question is
          whether you want to take that additional concentrated bet, or whether you'd prefer the diversification
          of the broader market. For this analysis, we assume a diversified portfolio.
        </p>
      </ReportSubsection>
    </ReportSection>
  );
};
